import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Guid } from "guid-typescript";
import { REHYDRATE } from "redux-persist";

import Datastore from "../../communication/Datastore";
import QueuedExport from "../../types/system/QueuedExport";

import store from "../../state/Store";
import QueuedExportFile from "../../types/system/QueuedExportFile";

// Define the type for our state store entry
interface ExportsState {
    data: QueuedExport[];
    status: string;
    error: string;
}

// Define the initial state for our state store entry
const initialState: ExportsState = {
    data: [],
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const exportsSlice = createSlice({
    name: "exports",
    initialState: initialState,
    reducers: {
        removeExport: (state, action: PayloadAction<Guid[]>) => {
            state.data = state.data.filter(
                item => !action.payload.some(id => id === item.id),
            );
        },
        resetExports(state) {
            state.data = [];
            state.status = "idle";
            state.error = null;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    data: (rawData?.payload?.exports?.data ?? []).map(
                        item => new QueuedExport(item),
                    ),
                    status:
                        !rawData?.payload?.exports ||
                        !rawData?.payload?.exports.length
                            ? "idle"
                            : rawData.payload.exports.status,
                    error: null,
                };
            })
            .addCase(fetchExports.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchExports.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.data = action.payload.map(
                    (item, index) => new QueuedExport(item, index),
                );
            })
            .addCase(fetchExports.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

const { removeExport, resetExports } = exportsSlice.actions;

// This is a thunk, they are used to load the state
export const fetchExports = createAsyncThunk(
    "exports/fetchExports",
    async () => {
        return await Datastore.getType<QueuedExport[]>(
            "api/SystemIO/Files/QueuedExports",
        );
    },
);
export const initialiseExports = createAsyncThunk(
    "exports/initialiseExports",
    async (_, thunkAPI) => {
        const { exports } = store.getState().db;

        if (exports.status === "idle") await thunkAPI.dispatch(fetchExports());
    },
);
export const addExports = createAsyncThunk(
    "exports/addExports",
    async (exportFiles: QueuedExportFile[], thunkAPI) => {
        await Datastore.post(`api/SystemIO/Files/QueuedExports`, exportFiles);

        thunkAPI.dispatch(resetExports());
    },
);
export const deleteExports = createAsyncThunk(
    "exports/deleteExports",
    async (exports: QueuedExport[], thunkAPI) => {
        await Promise.all(
            exports.map(exp =>
                Datastore.delete(
                    `api/SystemIO/Files/QueuedExports/${exp.id.toString()}`,
                ),
            ),
        );

        await thunkAPI.dispatch(removeExport(exports.map(exp => exp.id)));
    },
);
export const downloadExports = createAsyncThunk(
    "exports/downloadExports",
    async (exports: QueuedExport[], thunkAPI) => {
        (
            await Promise.all(
                exports.map(async exp => {
                    return {
                        name: exp.title,
                        data: await Datastore.get(
                            `api/SystemIO/Files/QueuedExports/${exp.id.toString()}/Compile`,
                        ),
                    };
                }),
            )
        ).download();
    },
);

// We have to export the reducer as this is added to the state store
export default exportsSlice.reducer;
